import React from 'react';
import { CommentContentElements, SeekToTimeFunction } from './commentUtils.types';
import styles from './commentUtils.module.css';

/**
 * コメント本文中の #xx:yy を検出し、クリック時に指定時間へシークできる要素を生成
 * また、@username 形式のメンションもハイライト表示
 * さらに、URLを検出してクリック可能なリンクとして表示
 *
 * @param {string} text - コメント本文
 * @param {SeekToTimeFunction} onSeekToTime - 動画の該当時間にシークする関数
 * @returns {CommentContentElements} - レンダリング可能な要素配列
 */
export function renderCommentContent(
  text: string,
  onSeekToTime?: SeekToTimeFunction,
  onMentionClick?: (username: string) => void
): CommentContentElements {
  // URLを検出する正規表現
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+\.[^\s]+)/g;
  
  // 時間スタンプとメンションの正規表現
  const combinedRegex = /#(\d{1,2}):(\d{2})(?=\s|$)|(\s|^)@([a-zA-Z0-9_.-]+@[a-zA-Z0-9_.-]+\.[a-zA-Z0-9_.-]+)(?=\s|$)|(\s|^)@([a-zA-Z0-9_.-]+)(?=\s|$)|(\s|^)@([^\s]+)(?=\s|$)|((https?:\/\/[^\s]+)|(www\.[^\s]+\.[^\s]+))/g;
  
  const elements: CommentContentElements = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = combinedRegex.exec(text)) !== null) {
    // マッチまでの文字列を追加
    if (match.index > lastIndex) {
      elements.push(text.substring(lastIndex, match.index));
    }
    
    const matchedText = match[0];
    const currentIndex = match.index;
    
    // #xx:yy の時間スタンプの場合
    if (matchedText.startsWith('#')) {
      const minutes = parseInt(match[1], 10);
      const seconds = parseInt(match[2], 10);

      elements.push(
        <span
          key={currentIndex}
          className="seek-token"
          onClick={() => {
            if (!Number.isNaN(minutes) && !Number.isNaN(seconds) && onSeekToTime) {
              onSeekToTime(minutes * 60 + seconds);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          title="クリックすると指定の時間へ移動し、画面の最上部にスクロールします"
        >
          {matchedText}
        </span>
      );
    } 
    // URLの場合
    else if (matchedText.match(urlRegex)) {
      let url = matchedText;
      
      // www.から始まる場合はhttpsを付加
      if (url.startsWith('www.')) {
        url = 'https://' + url;
      }

      // http/https 以外のスキームを持つURLはリンク化しない（XSS防御）
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        elements.push(matchedText);
        lastIndex = combinedRegex.lastIndex;
        continue;
      }

      elements.push(
        <a
          key={currentIndex}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="url-link"
          title="外部サイトを新しいタブで開きます"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {matchedText}
        </a>
      );
    }
    // @username のメンションの場合（メールアドレスを含む）
    else if (matchedText.includes('@')) {
      // メールアドレスの場合はグループ4、通常のユーザー名はグループ6か8にマッチ
      // 先頭の空白文字を取り除く
      const mentionText = matchedText.trimStart();
      const username = mentionText.substring(1); // @を取り除いたユーザー名
      
      elements.push(
        <span
          key={currentIndex}
          className="seek-token mention-token"
          title={`${username}さんへのメンション`}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation(); // イベントの伝播を停止
            // 視覚的なフィードバック
            const target = e.currentTarget as HTMLSpanElement;
            target.classList.add(styles['mention-token--active']);
            setTimeout(() => {
              target.classList.remove(styles['mention-token--active']);
            }, 300);

            // コールバックでメンションクリックを通知
            onMentionClick?.(username);
          }}
        >
          {mentionText}
        </span>
      );
      
      // 先頭の空白があった場合は追加
      if (matchedText !== mentionText) {
        const leadingSpace = matchedText.substring(0, matchedText.length - mentionText.length);
        elements[elements.length - 1] = (
          <React.Fragment key={currentIndex}>
            {leadingSpace}
            {elements[elements.length - 1]}
          </React.Fragment>
        );
      }
    }
    
    lastIndex = combinedRegex.lastIndex;
  }

  // 最後のマッチ以降の文字列を追加
  if (lastIndex < text.length) {
    elements.push(text.substring(lastIndex));
  }

  return elements;
} 